import React, { useEffect, useState } from "react"

import { Row, Col } from "reactstrap"

import Reviews from "./reviews"
import CreatePost from "./CreatePost"
import Notifications from "./notifications"
import Inbox from "./inbox"
import RecentActivity from "./recent-activity"
import WidgetUser from "./widget-user"
import { useSelector, useDispatch, connect } from "react-redux"
import YearlySales from "./yearly-sales"
import LatestTransactions from "./latest-transactions"
import LatestOrders from "./latest-orders"
import PostsList from "./posts_list"

//Import Action to copy breadcrumb items from local state to redux state
import { setBreadcrumbItems } from "../../store/actions"
import SnackbarComponent from "components/SnackBar"
import axios from "axios"
import PostDetails from "./PostDetails"
import Chat from "./chat"
import ReachChart from "./reach"
import ImpressionsChart from "./impressions"
import Miniwidgets from "pages/FacebookDashboard/MiniWidgets"
import config from "config/config"

const FacebookDashboard = props => {
  const [pageDetails, setPageDetails] = useState(null)
  const [err, setErr] = useState(null)
  const [viewMode, setViewMode] = useState("Month")
  const [success, setSuccess] = useState(null)
  const [fromDate, setFromDate] = useState("")
  const [toDate, setToDate] = useState("")
  const [loading, setLoading] = useState(false)
  const [reviews, setReviews] = useState([])
  const [postClicked, setPostClicked] = useState(false)
  const [selectedPost, setSelectedPost] = useState(null)
  const [posts, setPosts] = useState([])
  const [period, setPeriod] = useState("day")
  const [reports, setReports] = useState([])
  const [nextPage, setNextPage] = useState(null)
  const [pageToken, setPageToken] = useState(null)

  const activeOrganization = useSelector(
    state => state.organizationReducer.activeOrganization,
  )
  const { organizations, isConsolidatedView } = useSelector(
    state => state.organizationReducer,
  )
  const userData = useSelector(state => state.Login.userData)
  document.title = "Dashboard | AeraVue"

  const breadcrumbItems = [
    {
      title: `${activeOrganization.name} (${activeOrganization.type})`,
      link: "/dashboard",
    },
    { title: "Facebook", link: "#" },
  ]
  const fetchInsights = async () => {
    try {
      setLoading(true)

      // Get list of pages depending on view mode
      const fbPages = isConsolidatedView
        ? organizations.flatMap(org => org.Organization.FacebookIntegrations)
        : activeOrganization.FacebookIntegrations

      // SUM VARIABLES
      let totalFollowers = 0
      let totalLikes = 0
      let totalImpressionsCurrent = 0
      let totalImpressionsPrevious = 0
      let totalReachCurrent = 0
      let totalReachPrevious = 0

      // Utility
      const calcDiff = (current, previous) => {
        if (previous === 0) return "+0%"
        const diff = ((current - previous) / previous) * 100
        return `${diff >= 0 ? "+" : ""}${diff.toFixed(2)}%`
      }

      // ---------------------------------------
      // LOOP THROUGH EACH FACEBOOK PAGE
      // ---------------------------------------
      for (const page of fbPages) {
        const { pageId, pageToken } = page

        // Fetch followers_count and fan_count (page likes) directly as page
        // fields — the old `page_fans` INSIGHTS metric this used to call is
        // deprecated by Meta ("(#100) The value must be a valid insights
        // metric"); fan_count is the current equivalent and needs no
        // separate /insights request.
        const resp = await axios.get(
          `https://graph.facebook.com/v20.0/${pageId}`,
          {
            params: {
              access_token: pageToken,
              fields: "followers_count,fan_count",
            },
          },
        )

        totalFollowers += resp.data.followers_count || 0
        totalLikes += resp.data.fan_count || 0

        // =======================
        //   MONTH / WEEK MODES
        // =======================
        if (viewMode === "Month" || viewMode === "Week") {
          const period = viewMode === "Month" ? "days_28" : "week"

          const insightsResp = await axios.get(
            `https://graph.facebook.com/v20.0/${pageId}/insights`,
            {
              params: {
                access_token: pageToken,
                metric: "page_post_engagements,page_views_total",
                period,
              },
            },
          )

          const impressions = insightsResp.data.data[0]?.values || []
          const reach = insightsResp.data.data[1]?.values || []

          totalImpressionsCurrent += impressions[1]?.value || 0
          totalImpressionsPrevious += impressions[0]?.value || 0

          totalReachCurrent += reach[1]?.value || 0
          totalReachPrevious += reach[0]?.value || 0
        }

        // =======================
        //     CUSTOM RANGE
        // =======================
        else {
          let currentStart = new Date(fromDate)
          let currentEnd = new Date(toDate)
          const rangeDays = (currentEnd - currentStart) / (1000 * 60 * 60 * 24)

          const MAX_DAYS = 180
          if (rangeDays > MAX_DAYS) {
            setErr("Date range exceeded, max allowed is 6 months.")
            return
          }

          const GROUP = rangeDays > 28 ? 28 : rangeDays > 7 ? 7 : rangeDays

          for (let i = 0; i < rangeDays; i += GROUP) {
            const gStart = new Date(currentStart)
            gStart.setDate(gStart.getDate() + i)

            const gEnd = new Date(gStart)
            gEnd.setDate(gEnd.getDate() + GROUP - 1)
            if (gEnd > currentEnd) gEnd = currentEnd

            const pStart = new Date(gStart)
            pStart.setDate(pStart.getDate() - rangeDays)

            const pEnd = new Date(gEnd)
            pEnd.setDate(pEnd.getDate() - rangeDays)

            const currResp = await axios.get(
              `https://graph.facebook.com/v20.0/${pageId}/insights`,
              {
                params: {
                  access_token: pageToken,
                  metric: "page_post_engagements,page_views_total",
                  period: "day",
                  since: gStart / 1000,
                  until: gEnd / 1000,
                },
              },
            )

            const prevResp = await axios.get(
              `https://graph.facebook.com/v20.0/${pageId}/insights`,
              {
                params: {
                  access_token: pageToken,
                  metric: "page_post_engagements,page_views_total",
                  period: "day",
                  since: pStart / 1000,
                  until: pEnd / 1000,
                },
              },
            )

            const sum = vals =>
              vals.reduce((acc, item) => acc + (item.value || 0), 0)

            totalImpressionsCurrent += sum(currResp.data.data[0]?.values || [])
            totalImpressionsPrevious += sum(prevResp.data.data[0]?.values || [])

            totalReachCurrent += sum(currResp.data.data[1]?.values || [])
            totalReachPrevious += sum(prevResp.data.data[1]?.values || [])
          }
        }
      }

      // ---------------------------------------
      // SET FINAL REPORTS
      // ---------------------------------------
      setReports([
        {
          title: "Followers",
          iconClass: "account-group",
          total: totalFollowers,
          average: null,
          badgecolor: "info",
        },
        {
          title: "Likes",
          iconClass: "thumb-up",
          total: totalLikes,
          average: null,
          badgecolor: "info",
        },
        {
          title: "Page Views",
          iconClass: "message-draw",
          total: totalReachCurrent,
          average: calcDiff(totalReachCurrent, totalReachPrevious),
          badgecolor:
            totalReachCurrent >= totalReachPrevious ? "info" : "danger",
        },
        {
          title: "Engagements",
          iconClass: "star",
          total: totalImpressionsCurrent,
          average: calcDiff(totalImpressionsCurrent, totalImpressionsPrevious),
          badgecolor:
            totalImpressionsCurrent >= totalImpressionsPrevious
              ? "info"
              : "danger",
        },
      ])

      setLoading(false)
    } catch (err) {
      console.log(err)
      setErr("Error Fetching Insights.")
      setLoading(false)
    }
  }

  const fetchReviews = async () => {
    setLoading(true)

    try {
      // Determine which Facebook pages to use
      const fbPages = isConsolidatedView
        ? organizations.flatMap(org =>
            org.Organization.FacebookIntegrations.map(page => ({
              ...page,
              organizationName: org.Organization.name, // Store the org name
              organizationId: org.Organization.id,
              pageToken: org.Organization.FacebookIntegrations[0].pageToken,
            })),
          )
        : activeOrganization.FacebookIntegrations.map(page => ({
            ...page,
            organizationName: activeOrganization.name, // Single org name
            organizationId: activeOrganization.id,
            pageToken: activeOrganization.FacebookIntegrations[0].pageToken,
          }))

      let allReviews = []

      for (const page of fbPages) {
        const { pageId, pageToken, organizationName, organizationId } = page

        const response = await axios.get(
          `https://graph.facebook.com/v20.0/${pageId}/ratings`,
          {
            params: {
              access_token: pageToken,
              fields:
                "id,from,reviewer{name,picture},created_time,recommendation_type,review_text",
            },
          },
        )

        const reviewsWithOrg = (response.data.data || []).map(r => ({
          ...r,
          organizationName, // Attach org name here 🔥
          organizationId,
          pageId,
        }))

        // Merge back any previously-drafted reply / responded state saved
        // for this page's reviews (Facebook reviews aren't stored in our
        // DB, so this state lives in a small side table keyed by review id).
        if (reviewsWithOrg.length > 0) {
          try {
            const statusResp = await axios.get(
              `${config.backendUrl}/facebook/reviews/reply-status`,
              {
                params: {
                  pageId,
                  fbReviewIds: reviewsWithOrg.map(r => r.id).join(","),
                },
                headers: { Authorization: `Bearer ${userData.token}` },
              },
            )
            const statusById = new Map(
              (statusResp.data.replies || []).map(row => [row.fbReviewId, row]),
            )
            for (const r of reviewsWithOrg) {
              const saved = statusById.get(r.id)
              if (saved) {
                r.replyText = saved.replyText
                r.toneFlags = saved.toneFlags
                r.respondedManually = saved.respondedManually
              }
            }
          } catch (statusErr) {
            console.log("Error fetching reply status", statusErr)
          }
        }

        allReviews = [...allReviews, ...reviewsWithOrg]
      }

      setReviews(allReviews)
      setLoading(false)
    } catch (err) {
      console.log(err)
      setErr("Error Fetching reviews.")
      setLoading(false)
    }
  }

  const loadMorePosts = async () => {
    if (!nextPage) return // No more pages to load

    try {
      setLoading(true)
      const response = await axios.get(nextPage) // Use the next page URL
      console.log("More posts", response.data.data)

      setPosts(prevPosts => [...prevPosts, ...response.data.data]) // Append new posts
      setNextPage(response.data.paging?.next || null) // Update next page URL
      setLoading(false)
    } catch (err) {
      console.error("Error fetching more posts", err)
      setErr("Error loading more posts.")
    }
  }

  const fetchPosts = async () => {
    try {
      setLoading(true)

      // Determine which Facebook pages to fetch from
      const fbPages = isConsolidatedView
        ? organizations.flatMap(org =>
            org.Organization.FacebookIntegrations.map(page => ({
              ...page,
              organizationName: org.Organization.name,
            })),
          )
        : activeOrganization.FacebookIntegrations.map(page => ({
            ...page,
            organizationName: activeOrganization.name,
          }))

      let allPosts = []

      for (const page of fbPages) {
        const { pageId, pageToken, organizationName } = page

        const response = await axios.get(
          `https://graph.facebook.com/v20.0/${pageId}/posts`,
          {
            params: {
              fields:
                "id,message,full_picture,created_time,attachments{media_type,media,url},reactions.limit(100){name,id},comments.limit(50){message,created_time,from}",
              access_token: pageToken,
            },
          },
        )

        const postsWithOrg = (response.data.data || []).map(post => ({
          ...post,
          organizationName,
          pageId,
          pageToken,
        }))
        console.log("Fetched posts for page:", pageId, postsWithOrg)
        allPosts = [...allPosts, ...postsWithOrg]
      }

      setPosts(allPosts)
      setLoading(false)
    } catch (err) {
      console.error("Fetch posts error:", err)
      setErr("Error fetching posts.")
      setLoading(false)
    }
  }

  useEffect(() => {
    props.setBreadcrumbItems(`Facebook`, breadcrumbItems)
    fetchReviews()
    fetchPosts()
  }, [activeOrganization, isConsolidatedView, organizations])

  useEffect(() => {
    fetchInsights()
  }, [viewMode, activeOrganization, isConsolidatedView, organizations])

  return (
    <React.Fragment>
      <SnackbarComponent
        err={err}
        success={success}
        setErr={setErr}
        setSuccess={setSuccess}
      />
      {postClicked ? (
        <PostDetails
          pageToken={pageToken}
          postId={selectedPost}
          setErr={setErr}
          setSuccess={setSuccess}
          setPostClicked={setPostClicked}
          setSelectedPost={setSelectedPost}
        />
      ) : (
        <>
          <Miniwidgets
            reports={reports}
            setViewMode={setViewMode}
            viewMode={viewMode}
            loading={loading}
            callBack={fetchInsights}
            fromDate={fromDate}
            toDate={toDate}
            setFromDate={setFromDate}
            setToDate={setToDate}
          />
          <Row>
            <Col xl="6">
              {/* Monthly Earnings */}
              <ReachChart />
            </Col>

            <Col xl="6">
              {/* Email sent */}
              <ImpressionsChart />
            </Col>

            {/* <Col xl="3">
              <Notifications followersByCity={followersByCity} />
            </Col> */}
          </Row>
          <Row>
            <Col xl="12">
              {/* Monthly Earnings */}
              <Reviews reviews={reviews} loading={loading} />
            </Col>
            {/* <Col xl="3">
              <Notifications followersByCity={followersByCity} />
            </Col> */}
          </Row>
          <Row>
            <Col xl={12}>
              <PostsList
                reviews={posts}
                isConsolidatedView={isConsolidatedView}
                setPostClicked={setPostClicked}
                setSelectedPost={setSelectedPost}
                setPageToken={setPageToken}
              />
            </Col>
          </Row>
          {/* <Row>
            <Col xl="4" lg="6">
              <Inbox />
            </Col>
            <Col xl="4" lg="6">
              <RecentActivity />
            </Col>
            <Col xl="4">
              <WidgetUser />
              <YearlySales />
            </Col>
          </Row> */}
          {!isConsolidatedView && (
            <Row>
              <Col xl="12">
                <Chat setErr={setErr} />
              </Col>
            </Row>
          )}
        </>
      )}
    </React.Fragment>
  )
}

export default connect(null, { setBreadcrumbItems })(FacebookDashboard)
